import { parseUrl } from 'hub-http/helpers/url';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { getIsLocalDevelopment, getLocalizedHubSpotUrl, getPublicLocalizedPathname } from './urls';
import PortalIdParser from 'PortalIdParser';
import { getAppSettingsUrl } from 'integrations-lib/utils/connectedAppUtils';
const validLegalLangs = ['ja', 'de', 'en', 'es', 'pt', 'pt-br', 'fr'];
const getExternalLocalizedPath = (lang = 'en') => {
  switch (lang) {
    case 'ja':
      return '/jp';
    case 'pt':
    case 'pt-br':
      return '/br';
    case 'de':
    case 'es':
    case 'fr':
      return `/${lang}`;
    default:
      return '';
  }
};
export const getFormattedUrl = href => {
  if (!href) return undefined;
  const {
    protocol
  } = parseUrl(href);
  return !protocol ? `http://${href}` : href;
};
export const getFormattedShortlinkUrl = href => {
  if (!href) return href;
  const formattedHref = getFormattedUrl(href) || href;
  const portalId = PortalIdParser.get();
  if (portalId && formattedHref.includes('/l/') && formattedHref.match(new RegExp(/^(https?:\/\/)?app(-[a-z0-9]+)?\.hubspot(qa)?\.com/))) {
    const hrefWithParams = new URL(formattedHref || '');
    hrefWithParams.searchParams.append('portalId', portalId.toString());
    return hrefWithParams.toString();
  } else {
    return formattedHref;
  }
};
export const getHubSpotPricingUrl = (lang = 'en') => {
  return `${getLocalizedHubSpotUrl(lang)}/pricing`;
};
export const getHubSpotAiProductsTermsUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/product-specific-terms#AI-prod`;
};
export const getHubSpotBetaTermsUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/hubspot-beta-terms`;
};
export const getHubSpotPrivacyUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/privacy-policy`;
};
export const getHubSpotTermsOfServiceUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/terms-of-service`;
};
export const getHubSpotAcceptableUseUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/acceptable-use`;
};
export const getHubSpotSensitiveDataTermsUrl = (lang = 'en') => {
  const legalLang = validLegalLangs.includes(lang) ? lang : 'en';
  return `https://legal.hubspot.com${getExternalLocalizedPath(legalLang)}/sensitive-data-terms`;
};
export const getCategoryUrl = ({
  appContext,
  category,
  productType
}) => {
  const baseUrl = getFullUrl(appContext === 'app' ? 'app' : 'ecosystem', {
    hubletOverride: na1
  });
  if (appContext === 'app') {
    return `${baseUrl}/marketplace/${PortalIdParser.get()}/explore?eco_PRODUCT_TYPE=${productType}&eco_CATEGORY=${category}`;
  }
  return `${baseUrl}/marketplace/explore?eco_PRODUCT_TYPE=${productType}&eco_CATEGORY=${category}`;
};
export const getObjectiveUrl = ({
  appContext,
  category
}) => {
  const baseUrl = getFullUrl(appContext === 'app' ? 'app' : 'ecosystem', {
    hubletOverride: na1
  });
  if (appContext === 'app') {
    return `${baseUrl}/marketplace/${PortalIdParser.get()}/explore/workflow-templates?eco_OBJECTIVE=${category}`;
  }
  return `${baseUrl}/marketplace/explore/workflow-templates?eco_OBJECTIVE=${category}`;
};
export function getBreezeStudioUrl(agentType) {
  return `${getFullUrl('app')}/agent-hub/${PortalIdParser.get()}/manage-agents/${agentType === 'ASSISTANT' ? 'assistants' : 'agents'}`;
}
export function getAgentStudioUrl(instanceId) {
  return `${getFullUrl('app')}/agent-hub/${PortalIdParser.get()}/manage-agents/inbox?agentInstanceId=${instanceId}&filterAgentInstanceIds=${instanceId}`;
}
export function getAgentBuilderModernUrl(instanceId) {
  return `${getFullUrl('app')}/automation/${PortalIdParser.get()}/agent/${instanceId}`;
}
export const getDataSyncProductUrl = (lang = 'en') => `${getLocalizedHubSpotUrl(lang)}/products/data-sync`;
export const getRootUrl = appContext => {
  return `/marketplace${appContext === 'app' ? `/${PortalIdParser.get()}` : ''}`;
};
export function getConnectedAppsUrl() {
  const base = getFullUrl('app');
  return `${base}/connected-apps/${PortalIdParser.get()}/installed`;
}
export function getSettingsRouterUrl({
  appId
}) {
  return `${getFullUrl('app')}/integration/${PortalIdParser.get()}/settings/${appId}`;
}
export function getInstalledAppOverviewUrl(appId) {
  return `${getFullUrl('app')}${getAppSettingsUrl({
    appId
  })}`;
}

//util used to handle embedded (iframe) vs. non-embedded routing
export function navigateTo(url, isEmbedded) {
  if (isEmbedded) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}
export function getMarketplaceThirdPartyAuthStateUrl(portalId) {
  return `${getFullUrl(getIsLocalDevelopment() ? 'local' : 'app')}/marketplace/${portalId}/third-party-auth-state`;
}
export function getMarketplaceInstallSuccessUrl(portalId) {
  return `${getFullUrl(getIsLocalDevelopment() ? 'local' : 'app')}/marketplace/${portalId}/install-success`;
}

// shortlinks will always take you to the in-app details page
export function getAppDetailsShortlink(slug) {
  return `${getFullUrl('app')}/l/marketplace/listing/${slug}`;
}
export function getSolutionsPartnerDetailsShortlink(slug) {
  return `${getFullUrl('app')}/l/marketplace/solutions/${slug}`;
}
export function getPublicAppDetailsUrl(slug) {
  return `${getFullUrl('ecosystem')}/marketplace/listing/${slug}`;
}
export function getPublicLocalizedListingDetailsUrl(slug, language) {
  const languagePrefix = getPublicLocalizedPathname(language);
  return `${getFullUrl('ecosystem')}${languagePrefix}/marketplace/listing/${slug}`;
}
export function getAppDetailsUrl(appContext, slug) {
  return appContext === 'app' ? getAppDetailsShortlink(slug) : getPublicAppDetailsUrl(slug);
}
export function getAppFeatureDiscoveryUrl(appId, portalId) {
  return `${getFullUrl('app')}${getAppSettingsUrl({
    appId,
    portalId
  })}/featureDiscovery`;
}
export function getAppOverviewUrl(appId, portalId) {
  return `${getFullUrl('app')}${getAppSettingsUrl({
    appId,
    portalId
  })}/overview`;
}
export function getAppAppCardsUrl(appId, portalId) {
  return `${getFullUrl('app')}${getAppSettingsUrl({
    appId,
    portalId
  })}/app-cards`;
}
export function getApprovedAppsUrl() {
  return `${getFullUrl('app')}/connected-apps/${PortalIdParser.get()}/approved-apps`;
}
export function getCreditsUsageUrl(appContext) {
  if (appContext === 'app') {
    return `${getFullUrl('app')}/account-and-billing/${PortalIdParser.get()}/usage/hubspot-credits`;
  }
  return 'https://knowledge.hubspot.com/account-management/understand-hubspot-credits-and-billing';
}
export function getWorkflowEnrollmentKBArticle() {
  return 'https://knowledge.hubspot.com/workflows/set-your-workflow-enrollment-triggers';
}
export function getWorkflowFlowUrl(flowId, isUngatedForUnifiedBuilder, targetType) {
  const unifiedBuilderUrl = `${getFullUrl('app')}/automation/${PortalIdParser.get()}/flow/${flowId}`;
  const legacyBuilderUrl = `${getFullUrl('app')}/workflows/${PortalIdParser.get()}/platform/flow/${flowId}/edit`;
  if (targetType === 'UNIFIED_BUILDER' && isUngatedForUnifiedBuilder) {
    return unifiedBuilderUrl;
  }
  return legacyBuilderUrl;
}

// This function should be used to handle logic for when to open the embedded listing details modal.
// If already in embedded view, we should avoid opening the modal
export function openListingModalOrListingInNewPage(slug, isEmbedded, openListingDetailsModal) {
  if (isEmbedded) {
    const url = getFormattedShortlinkUrl(getAppDetailsUrl('app', slug));
    navigateTo(url, isEmbedded);
  } else {
    openListingDetailsModal(slug);
  }
}
export function getAllWorkflowsUrl() {
  return `${getFullUrl('app')}/workflows/${PortalIdParser.get()}/view/default`;
}